import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatRp } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowUUpLeft } from "@phosphor-icons/react";

const OPTIONS = [
  { key: "none", label: "Tidak Refund", desc: "Reservasi dibatalkan tanpa pengembalian dana." },
  { key: "partial", label: "Refund Sebagian", desc: "Kembalikan sebagian dana yang sudah dibayar." },
  { key: "full", label: "Refund Penuh", desc: "Kembalikan seluruh dana yang sudah dibayar." },
];

export default function RefundDialog({ reservation, open, onClose, onDone }) {
  const r = reservation || {};
  const paid = Number(r.total_dibayar || 0);
  const [type, setType] = useState("none");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setType("none"); setAmount(""); setReason(""); } }, [open]);

  const refund = type === "full" ? paid : type === "partial" ? Number(amount || 0) : 0;
  const net = paid - refund;

  const submit = async () => {
    if (type === "partial" && (!(refund > 0) || refund > paid)) return toast.error("Nominal refund harus lebih dari 0 dan tidak melebihi total dibayar");
    if (type === "partial" && !reason.trim()) return toast.error("Alasan refund sebagian wajib diisi");
    setBusy(true);
    try {
      await api.post(`/reservations/${r.id}/cancel-refund`, { refund_type: type, amount: refund, reason });
      toast.success("Reservasi dibatalkan & refund diproses");
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="refund-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ArrowUUpLeft size={20} weight="duotone" className="text-rose-500" /> Batalkan &amp; Refund</DialogTitle></DialogHeader>

        <div className="rounded-xl bg-[#FAF7FF] p-3 text-sm">
          <p className="font-bold text-slate-800">{r.nama_lengkap} · {r.jenis_layanan}</p>
          <div className="mt-1 flex justify-between"><span className="text-slate-500">Total Dibayar</span><span className="font-bold text-emerald-600" data-testid="refund-paid">{formatRp(paid)}</span></div>
        </div>

        <div className="space-y-2">
          {OPTIONS.map((o) => (
            <label key={o.key} data-testid={`refund-type-${o.key}`} className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 ${type === o.key ? "border-[#7C4DE0] bg-[#EEE6FF]" : "border-slate-200"}`}>
              <input type="radio" name="refund-type" checked={type === o.key} onChange={() => setType(o.key)} className="mt-1" />
              <div><p className="text-sm font-bold text-slate-800">{o.label}</p><p className="text-xs text-slate-500">{o.desc}</p></div>
            </label>
          ))}
        </div>

        {type === "partial" && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Nominal Refund (Rp) *</label>
            <input data-testid="refund-amount" type="number" min="0" max={paid} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="cth. 50000" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-500">Alasan {type === "partial" ? "*" : "(opsional)"}</label>
          <textarea data-testid="refund-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan pembatalan / refund" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        </div>

        <div className="rounded-xl border border-slate-200 p-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Nominal Refund</span><span className="font-bold text-rose-600" data-testid="refund-preview">{formatRp(refund)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Net Payment (setelah refund)</span><span className="font-bold text-slate-800" data-testid="refund-net">{formatRp(net)}</span></div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
          <button data-testid="refund-submit" disabled={busy} onClick={submit} className="rounded-xl bg-rose-500 px-5 py-2 text-sm font-bold text-white hover:bg-rose-600 disabled:opacity-60">Proses Pembatalan</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
